import React, { useState } from 'react';
import { Tabs as AntdTabs } from 'antd';
import { useTranslation } from '../../../../../core';
import { css, colors, fonts, spacing, StyleSheet } from '../../../../../../styles';
import { useIntegrationFieldGroups } from '../../hooks/useIntegrationFieldGroups';
import { TAB_FIELDS, TABS } from '../../utils/constants';
import { AdministrationTab } from './AdministrationTab';
import { GeneralTab } from './GeneralTab';
import { LogsTab } from './LogsTab';
import { SmtpTab } from './SmtpTab';

/**
 * Organizes integration details into focused tabs with independent save actions.
 *
 * @param {object} props
 * @param {string} props.companyId
 * @param {Function} props.hasChanges
 * @param {object} props.integration
 * @param {boolean} props.isSaving
 * @param {Function} props.onChangeProperty
 * @param {Function|null} props.onClose
 * @param {Function} props.onSaveFields
 * @param {object} props.updatedData
 */
export const IntegrationDetailsTabs = ({
    companyId,
    hasChanges,
    integration,
    isSaving = false,
    onChangeProperty,
    onClose = null,
    onSaveFields,
    updatedData
}) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(TABS.general);
    const fieldGroups = useIntegrationFieldGroups(integration, onChangeProperty);

    const commonTabProps = {
        integration,
        isSaving,
        onClose,
        updatedData
    };

    return (
        <AntdTabs
            activeKey={activeTab}
            onChange={setActiveTab}
            animated={false}
            className={css(styles.tabs)}>
            <AntdTabs.TabPane tab={t('integrations.tabs.general')} key={TABS.general}>
                <GeneralTab
                    {...commonTabProps}
                    fields={fieldGroups.general}
                    hasChanges={hasChanges(TAB_FIELDS[TABS.general])}
                    onSave={() => onSaveFields(TAB_FIELDS[TABS.general])}
                    synchronizationFields={fieldGroups.synchronization}
                />
            </AntdTabs.TabPane>
            <AntdTabs.TabPane tab={t('integrations.tabs.smtp')} key={TABS.smtp}>
                <SmtpTab
                    {...commonTabProps}
                    fields={fieldGroups.smtp}
                    hasChanges={hasChanges(TAB_FIELDS[TABS.smtp])}
                    onSave={() => onSaveFields(TAB_FIELDS[TABS.smtp])}
                />
            </AntdTabs.TabPane>
            <AntdTabs.TabPane
                tab={t('integrations.tabs.administration')}
                key={TABS.administration}>
                <AdministrationTab
                    {...commonTabProps}
                    companyId={companyId}
                    fields={fieldGroups.blocking}
                    hasChanges={hasChanges(TAB_FIELDS[TABS.administration])}
                    onSave={() => onSaveFields(TAB_FIELDS[TABS.administration])}
                />
            </AntdTabs.TabPane>
            <AntdTabs.TabPane tab={t('integrations.tabs.logs')} key={TABS.logs}>
                <LogsTab companyId={companyId} integrationId={integration.id} />
            </AntdTabs.TabPane>
        </AntdTabs>
    );
};

const styles = StyleSheet.create({
    tabs: {
        '& .ant-tabs-bar': {
            marginBottom: spacing.s4
        },
        '& .ant-tabs-nav-wrap': {
            overflowX: 'auto',
            overflowY: 'hidden'
        },
        '& .ant-tabs-nav-scroll': {
            overflow: 'visible'
        },
        '& .ant-tabs-nav': {
            display: 'inline-flex',
            whiteSpace: 'nowrap'
        },
        '& .ant-tabs-tab': {
            ...fonts.smNormal,
            marginRight: spacing.s4
        },
        '& .ant-tabs-tab-active': {
            ...fonts.smBold,
            color: colors.primary
        },
        '@media (max-width: 640px)': {
            '& .ant-tabs-tab': {
                marginRight: spacing.s3
            }
        }
    }
});
